import { trpc } from "@/common/helpers/trpc";
import { useUrlBuilder } from "@/common/hooks/useUrlBuilder";
import { useContext } from "react";

import { PaymentMethod, ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE } from "@simplyk/common";
import { UseFormSetValue } from "react-hook-form";

import { PaymentErrorReport, unknownPaymentErrorCode } from "./helper";
import { HandlePostSubmit } from "./useStripePayment";

import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DonationFormPaymentInput } from "@/common/types/donationForm";
import { TicketingPaymentInput } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { SubmitCommandFormInput } from "@/components/PaymentProcessor/helper";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useSubmitCommand } from "@/components/PaymentProcessor/hooks/useSubmitCommand";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { isPaymentFailed } from "@/components/PaymentProcessor/type";

export const useOfflinePayment = ({
  setValue,
  handlePaymentError,
  handlePostSubmit,
  onDafSubmitted,
}: {
  setValue: UseFormSetValue<DonationFormPaymentInput | TicketingPaymentInput>;
  handlePaymentError: (report: PaymentErrorReport) => void;
  handlePostSubmit: HandlePostSubmit;
  onDafSubmitted: (providerId: string | undefined) => void;
}) => {
  const { handleSubmitCommand } = useSubmitCommand({ setValue });
  const { t } = useTranslate();
  const buildUrl = useUrlBuilder();
  const trpcUtils = trpc.useUtils();
  const { commandId, formType, setDafFlow } = useContext(FrontendFormContext);

  const payOffline = async (submitCommandInput: SubmitCommandFormInput): Promise<string | undefined> => {
    const pm = submitCommandInput.paymentMethod;
    const express = Boolean(submitCommandInput.isExpressCheckout);

    const submitResponse = await handleSubmitCommand(submitCommandInput);
    if (submitResponse && isPaymentFailed(submitResponse)) {
      handlePaymentError({
        rawErrorCode: submitResponse.error.code || "server_submit_error",
        rawErrorMessage:
          submitResponse.error.code === ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE
            ? ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE
            : submitResponse.error.message,
        step: "server-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId,
        isKnownError: true,
      });
      return;
    }

    if (pm === PaymentMethod.Daf) {
      const token = submitResponse.data?.dafPaymentToken;
      if (!token) {
        handlePaymentError({
          rawErrorCode: "missing_daf_token",
          rawErrorMessage: t("common", "unknown_error", { code: "DAF_TOKEN" }),
          step: "server-submit",
          paymentMethod: pm,
          isExpressCheckout: express,
          commandId,
          isPaymentSucceeded: true,
          isKnownError: false,
        });
        return;
      }
      onDafSubmitted(
        "dafProviderId" in submitCommandInput.paymentInput ? submitCommandInput.paymentInput.dafProviderId : undefined
      );
      window.history.replaceState(null, "", buildUrl({ searchParams: (params) => ({ ...params, dafToken: token }) }));
      await trpcUtils.getDafPayment.fetch({ token }).catch(() => undefined);
      setDafFlow({ token, isResume: false, emailUpdateToken: submitResponse.data?.emailUpdateToken });
      return;
    }

    const command = submitResponse.data?.command;
    const transaction = submitResponse.data?.transaction ?? null;
    const emailUpdateToken = submitResponse.data?.emailUpdateToken;
    const buyerTicketToken = submitResponse.data?.buyerTicketToken;
    if (!command) {
      handlePaymentError({
        rawErrorCode: "missing_command",
        rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.offline1.code }),
        step: "server-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId,
        isKnownError: false,
      });
      return;
    }

    const { paymentInput } = submitCommandInput;
    const result = await handlePostSubmit({
      paymentInput,
      command,
      transaction,
      formType,
      emailUpdateToken,
      buyerTicketToken,
    });
    if (result.returnUrl) {
      return result.returnUrl;
    }

    if (result.error) {
      handlePaymentError({
        rawErrorCode: "post_submit_error",
        rawErrorMessage: result.error,
        step: "post-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId: command.id,
        isPaymentSucceeded: true,
        isKnownError: false,
      });
    } else {
      handlePaymentError({
        rawErrorCode: "missing_return_url",
        rawErrorMessage: t("common", "unknown_error.paymentSucceeded", { code: unknownPaymentErrorCode.offline2.code }),
        step: "post-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId: command.id,
        isPaymentSucceeded: true,
        isKnownError: false,
      });
    }
  };

  return { payOffline };
};
